import { useCallback, useEffect, useRef, useState } from "react";
import { GripVertical } from "lucide-react";

const ZOOM_KEY = "portal_text_zoom_v1";
const POS_KEY = "portal_text_zoom_pos_v1";
const STEPS = [85, 90, 95, 100, 110, 120, 135, 150];
const DEFAULT_ZOOM = 100;

/**
 * Floating Text Zoom Control — accessibilità globale del portale.
 * Sempre visibile, posizione trascinabile con mouse/touch (persistita in localStorage).
 * Tasto destro → ripristina posizione predefinita (top-right).
 */
export function TextZoomControl() {
  const [zoom, setZoom] = useState(() => {
    try {
      const raw = parseInt(localStorage.getItem(ZOOM_KEY) || "", 10);
      if (!isNaN(raw) && STEPS.includes(raw)) return raw;
    } catch { /* noop */ }
    return DEFAULT_ZOOM;
  });
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem(POS_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p?.x === "number" && typeof p?.y === "number") return p;
      }
    } catch { /* noop */ }
    return null;
  });
  const ref = useRef(null);
  // Pos ref per evitare stale closure dentro i listener
  const posRef = useRef(pos);
  useEffect(() => { posRef.current = pos; }, [pos]);

  // Applica zoom al root
  useEffect(() => {
    document.documentElement.style.fontSize = `${zoom}%`;
    try { localStorage.setItem(ZOOM_KEY, String(zoom)); } catch { /* noop */ }
  }, [zoom]);

  const clamp = useCallback((x, y) => {
    const w = ref.current?.offsetWidth || 140;
    const h = ref.current?.offsetHeight || 30;
    const maxX = Math.max(0, window.innerWidth - w - 4);
    const maxY = Math.max(0, window.innerHeight - h - 4);
    return { x: Math.min(Math.max(4, x), maxX), y: Math.min(Math.max(4, y), maxY) };
  }, []);

  // Drag handling — listener si registrano solo quando il drag parte
  const startDrag = useCallback((clientX, clientY) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const offsetX = clientX - r.left;
    const offsetY = clientY - r.top;
    let moved = false;

    const onMove = (cx, cy) => {
      if (!moved && (Math.abs(cx - clientX) > 4 || Math.abs(cy - clientY) > 4)) moved = true;
      if (moved) {
        const next = clamp(cx - offsetX, cy - offsetY);
        setPos(next);
        posRef.current = next;
      }
    };
    const onMouseMove = (e) => onMove(e.clientX, e.clientY);
    const onTouchMove = (e) => { const t = e.touches[0]; if (t) onMove(t.clientX, t.clientY); };
    const endDrag = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", endDrag);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", endDrag);
      window.removeEventListener("touchcancel", endDrag);
      if (moved && posRef.current) {
        try { localStorage.setItem(POS_KEY, JSON.stringify(posRef.current)); } catch { /* noop */ }
      }
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", endDrag);
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", endDrag);
    window.addEventListener("touchcancel", endDrag);
  }, [clamp]);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    startDrag(e.clientX, e.clientY);
  };
  const onTouchStart = (e) => {
    const t = e.touches[0]; if (!t) return;
    e.stopPropagation();
    startDrag(t.clientX, t.clientY);
  };

  // Re-clamp on window resize
  useEffect(() => {
    const onResize = () => {
      if (!posRef.current) return;
      const next = clamp(posRef.current.x, posRef.current.y);
      setPos(next);
      posRef.current = next;
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clamp]);

  const idx = STEPS.indexOf(zoom);
  const dec = () => { if (idx > 0) setZoom(STEPS[idx - 1]); };
  const inc = () => { if (idx >= 0 && idx < STEPS.length - 1) setZoom(STEPS[idx + 1]); };
  const reset = () => setZoom(DEFAULT_ZOOM);
  const resetPos = (e) => {
    e.preventDefault();
    if (pos) {
      setPos(null);
      posRef.current = null;
      try { localStorage.removeItem(POS_KEY); } catch { /* noop */ }
    }
  };

  return (
    <div
      ref={ref}
      data-testid="text-zoom-control"
      onContextMenu={resetPos}
      style={pos
        ? { left: `${pos.x}px`, top: `${pos.y}px`, fontSize: "12px" }
        : { top: "8px", right: "8px", fontSize: "12px" }}
      className="fixed z-[55] flex items-center gap-0.5 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-full shadow-sm pl-0.5 pr-1 py-0.5 select-none touch-none"
    >
      {/* Maniglia di trascinamento dedicata — l'unica area da cui parte il drag */}
      <span
        data-testid="text-zoom-handle"
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        title="Trascina per spostare · Tasto destro per ripristinare"
        className="h-6 w-5 inline-flex items-center justify-center rounded-l-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 cursor-grab active:cursor-grabbing"
        aria-label="Trascina il controllo zoom"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </span>
      <button type="button" data-testid="text-zoom-dec" onClick={dec} disabled={idx <= 0}
        className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-30 text-slate-700 font-semibold"
        aria-label="Riduci testo">A−</button>
      <button type="button" data-testid="text-zoom-reset" onClick={reset}
        className={`h-6 px-2 inline-flex items-center justify-center rounded-full hover:bg-slate-100 font-medium ${zoom === DEFAULT_ZOOM ? "text-slate-500" : "text-blue-600"}`}
        aria-label="Reimposta zoom">{zoom}%</button>
      <button type="button" data-testid="text-zoom-inc" onClick={inc} disabled={idx < 0 || idx >= STEPS.length - 1}
        className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-slate-100 disabled:opacity-30 text-slate-700 font-bold text-sm"
        aria-label="Ingrandisci testo">A+</button>
    </div>
  );
}

export default TextZoomControl;
